import { PatientSurvey } from '../types/PatientSurvey.type';
import { SurveyQuestion, SurveyQuestionAnswerNonGroup } from '../types/Survey.type';

const resolveValueForAnswer = (
  question: SurveyQuestion,
  answer: SurveyQuestionAnswerNonGroup | undefined
): string => {
  if (!answer) return '';

  switch (answer.type) {
    case 'radio': {
      if (answer.value == null) return '';

      if (String(answer.value) === 'other') {
        return answer.otherValue || '';
      }

      const match = question.options?.find(o => String(o.id) === String(answer.value));

      return match?.display ?? String(answer.value);
    }
    case 'checkbox': {
      const vals = answer.value;
      if (!Array.isArray(vals) || vals.length === 0) return '';
      const displays = vals
        .map(v => {
          if (String(v) === 'other') {
            return answer.otherValue || '';
          }

          const match = question.options?.find(o => String(o.id) === String(v));

          return match?.display ?? String(v);
        })
        .filter(Boolean);

      return displays.join(', ');
    }
    case 'slider':
      return answer.value != null ? String(answer.value) : '';
    case 'input':
    case 'textarea':
      return answer.value != null ? String(answer.value) : '';
    case 'date': {
      if (!answer.value) return '';
      if (answer.value.exact) return answer.value.exact;

      if (answer.value.estimate) {
        return `${answer.value.estimate.value} ${answer.value.estimate.unit} ago`;
      }

      return '';
    }
    case 'bodypicker':
      return '';
    default:
      return '';
  }
};

/**
 * Flatten survey questions into a flat list where group questions
 * are replaced by their sub-questions in order.
 */
export const flattenQuestions = (questions: SurveyQuestion[]): SurveyQuestion[] => {
  const flat: SurveyQuestion[] = [];

  for (const q of questions) {
    if (q.type === 'group') {
      const subs = q.typeOptions?.subQuestions || [];

      for (const sub of subs) {
        flat.push(sub);
      }
    } else {
      flat.push(q);
    }
  }

  return flat;
};

/**
 * Resolve the answer value for a question, pulling from group answers if needed.
 */
const resolveQuestionAnswer = (question: SurveyQuestion, questions: SurveyQuestion[]): string => {
  // Check if this question is a direct top-level question with its own answer
  if (question.answer && question.answer.type !== 'group') {
    return resolveValueForAnswer(question, question.answer as SurveyQuestionAnswerNonGroup);
  }

  // Otherwise look for it as a sub-question inside a group
  for (const q of questions) {
    if (q.type === 'group' && q.answer?.type === 'group') {
      const groupValue = q.answer.value;
      const subAnswer = groupValue?.[question.id] as SurveyQuestionAnswerNonGroup | undefined;

      if (subAnswer) {
        return resolveValueForAnswer(question, subAnswer);
      }
    }
  }

  return '';
};

export const hasSubjectiveTemplate = (survey: PatientSurvey): boolean => {
  return !!survey?.survey?.subjectiveTemplate;
};

/**
 * Build an answer-lookup function for the flat question list.
 * Returns a function: (1-based index) => resolved answer string.
 */
const buildAnswerLookup = (
  flat: SurveyQuestion[],
  questions: SurveyQuestion[]
): ((idx: number) => string) => {
  return (idx: number) => {
    if (idx < 0 || idx >= flat.length) return '';

    return resolveQuestionAnswer(flat[idx], questions);
  };
};

/**
 * Build a Map<questionId, SurveyQuestion> from all questions,
 * including sub-questions inside groups.
 */
const buildQuestionByIdMap = (questions: SurveyQuestion[]): Map<string, SurveyQuestion> => {
  const map = new Map<string, SurveyQuestion>();

  for (const q of questions) {
    map.set(q.id, q);
    if (q.templateAlias) map.set(q.templateAlias, q);

    if (q.type === 'group') {
      for (const sub of q.typeOptions?.subQuestions || []) {
        map.set(sub.id, sub);
        if (sub.templateAlias) map.set(sub.templateAlias, sub);
      }
    }
  }

  return map;
};

/**
 * Apply pipe transforms to a resolved answer value.
 *
 * Supported pipes:
 *   |lower            — lowercase
 *   |yesno:yes:no     — map Yes/No to custom text
 *   |percent_desc     — numeric percentage → descriptor
 *   |join:,           — join (already joined by resolveValueForAnswer for checkboxes)
 *   |date             — passthrough (already formatted by resolveValueForAnswer)
 */
const applyPipes = (value: string, pipes: string): string => {
  let result = value;

  for (const pipe of pipes.split('|').filter(Boolean)) {
    if (pipe === 'lower') {
      result = result.toLowerCase();
    } else if (pipe.startsWith('yesno:')) {
      const parts = pipe.split(':');
      const yesText = parts[1] || '';
      const noText = parts[2] || '';

      result = result === 'Yes' ? yesText : noText;
    } else if (pipe === 'percent_desc') {
      const n = Number(result);

      if (isNaN(n)) {
        result = '';
      } else {
        result = n < 20 ? 'rarely' : n < 40 ? 'moderately' : n < 60 ? 'frequently' : 'continually';
      }
    } else if (pipe.startsWith('join:')) {
      const sep = pipe.slice(5);

      result = result.split(', ').join(sep);
    } else if (pipe === 'date') {
      // Already formatted by resolveValueForAnswer
    }
    // 'bullets' is handled at the call site
  }

  return result;
};

/**
 * Resolve answer for a question by its ID, using the original questions array
 * (needed to look up group sub-question answers).
 */
const resolveAnswerById = (
  questionId: string,
  idMap: Map<string, SurveyQuestion>,
  questions: SurveyQuestion[]
): string => {
  const question = idMap.get(questionId);

  if (!question) return '';

  return resolveQuestionAnswer(question, questions);
};

/**
 * Template syntax:
 *   {title}                     — survey name
 *   {qN}                        — question text for the Nth flattened question (1-based)
 *   {aN}                        — answer value for the Nth flattened question (1-based)
 *   {#aN}...{/aN}               — conditional block: omitted when aN is blank
 *   {id:UUID}                   — answer value by question ID
 *   {id:UUID|pipe}              — answer value by question ID with pipe transform
 *   {qid:UUID}                  — question text by question ID
 *   {#id:UUID}...{/id:UUID}     — conditional block by ID: omitted when answer is blank
 *   {#id:UUID=val}...{/id:UUID} — value-conditional block: only if answer matches val
 *
 * Pipe transforms: lower, yesno:yes:no, percent_desc, join:sep, date
 *
 * Blank placeholders outside conditional blocks resolve to empty string.
 * Multiple consecutive spaces are collapsed and the result is trimmed.
 */
export const buildSubjectiveFromTemplate = (survey: PatientSurvey): string => {
  const template = survey?.survey?.subjectiveTemplate;
  if (!template) return '';

  const questions = survey?.survey?.questions || [];
  const flat = flattenQuestions(questions);
  const getAnswer = buildAnswerLookup(flat, questions);
  const idMap = buildQuestionByIdMap(questions);

  const title = survey?.survey?.english?.name || survey?.survey?.name || '';

  let result = template;

  // Process all conditional blocks in a loop so that nested conditionals
  // (e.g. {#id:A}{#id:B}...{/id:B}{/id:A}) are resolved from outside-in.
  const MAX_PASSES = 5;
  for (let pass = 0; pass < MAX_PASSES; pass++) {
    const before = result;

    // 0a. Process includes-conditional blocks {#id:UUID~value}...{/id:UUID}
    // For multi-select questions, checks if the answer includes the given value.
    result = result.replace(
      /\{#id:([\w-]+)~([^}]+)\}([\s\S]*?)\{\/id:\1\}/gi,
      (_match, id, expectedValue, inner) => {
        const question = idMap.get(id);
        if (!question) return '';

        const answer = resolveQuestionAnswer(question, questions);
        if (!answer) return '';

        // Check if any of the comma-separated values matches
        const answerValues = answer.split(', ').map(v => v.toLowerCase());
        if (!answerValues.includes(expectedValue.toLowerCase())) return '';

        return inner;
      }
    );

    // 0b. Process negated conditional blocks {!id:UUID}...{/!id:UUID}
    // Renders content only when the question has NO answer.
    result = result.replace(/\{!id:([\w-]+)\}([\s\S]*?)\{\/!id:\1\}/gi, (_match, id, inner) => {
      const answer = resolveAnswerById(id, idMap, questions);
      if (answer) return '';

      return inner;
    });

    // 1a. Process index-based conditional blocks {#aN}...{/aN}
    result = result.replace(/\{#a(\d+)\}([\s\S]*?)\{\/a\1\}/g, (_match, numStr, inner) => {
      const idx = parseInt(numStr, 10) - 1;
      const answer = getAnswer(idx);

      if (!answer) return '';

      return inner;
    });

    // 1b. Process value-conditional blocks {#id:UUID=value}...{/id:UUID}
    result = result.replace(
      /\{#id:([\w-]+)=([^}]+)\}([\s\S]*?)\{\/id:\1\}/gi,
      (_match, id, expectedValue, inner) => {
        const answer = resolveAnswerById(id, idMap, questions);

        if (!answer || answer.toLowerCase() !== expectedValue.toLowerCase()) return '';

        return inner;
      }
    );

    // 1c. Process ID-based conditional blocks {#id:UUID}...{/id:UUID}
    result = result.replace(/\{#id:([\w-]+)\}([\s\S]*?)\{\/id:\1\}/gi, (_match, id, inner) => {
      const answer = resolveAnswerById(id, idMap, questions);

      if (!answer) return '';

      return inner;
    });

    // If nothing changed this pass, all conditionals are resolved
    if (result === before) break;
  }

  // 2. Replace {title}
  result = result.replace(/\{title\}/g, title);

  // 3. Replace {qN} — question text by index
  result = result.replace(/\{q(\d+)\}/g, (_match, numStr) => {
    const idx = parseInt(numStr, 10) - 1;
    if (idx < 0 || idx >= flat.length) return '';

    return (flat[idx].question || '').replace(/<[^>]*>/g, '');
  });

  // 4. Replace {aN} — answer value by index
  result = result.replace(/\{a(\d+)\}/g, (_match, numStr) => {
    const idx = parseInt(numStr, 10) - 1;

    return getAnswer(idx);
  });

  // 5. Replace {qid:UUID} — question text by ID
  result = result.replace(/\{qid:([\w-]+)\}/gi, (_match, id) => {
    const question = idMap.get(id);

    return (question?.question || '').replace(/<[^>]*>/g, '');
  });

  // 6. Replace {id:UUID} and {id:UUID|pipes} — answer value by ID with optional pipes
  result = result.replace(/\{id:([\w-]+)(?:\|([^}]+))?\}/gi, (_match, id, pipes) => {
    const answer = resolveAnswerById(id, idMap, questions);

    if (pipes && pipes.split('|').includes('bullets')) {
      // Produce one "- value" line per comma-separated value
      if (!answer) return '';
      return answer
        .split(', ')
        .filter(Boolean)
        .map(v => `- ${v}`)
        .join('\n');
    }

    if (pipes) {
      return applyPipes(answer, pipes);
    }

    return answer;
  });

  // 7. Collapse multiple horizontal spaces, remove blank lines where the conditional
  //    block resolved to nothing, and trim each line.
  result = result
    .split('\n')
    .map(line => line.replace(/ {2,}/g, ' ').trim())
    .filter((line, i, arr) => {
      // Remove blank lines that follow another blank line (collapse to single blank line)
      if (line === '' && i > 0 && arr[i - 1] === '') return false;

      return true;
    })
    .join('\n')
    .trim();

  return result;
};

/**
 * Convert processed template text to HTML with proper structure:
 * - `## Title` lines → bold section header `<p><span style="font-weight: bold;">Title</span></p>`
 * - `- content` lines → grouped into `<ul><li>content</li>...</ul>`
 * - Blank lines → skip (section separator)
 * - Other lines → `<p>line</p>` (backward compat)
 * Post-process: remove orphaned headers (header with no content following).
 */
function renderTemplateLines(text: string): string {
  const lines = text.split('\n');
  const htmlParts: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trim();

    if (line === '') {
      i++;
      continue;
    }

    if (line.startsWith('## ')) {
      const title = line.slice(3).trim();
      htmlParts.push(`<p><span style="font-weight: bold;">${title}</span></p>`);
      i++;
      continue;
    }

    if (line.startsWith('- ')) {
      const lis: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('- ')) {
        lis.push(`<li>${lines[i].trim().slice(2).trim()}</li>`);
        i++;
      }
      htmlParts.push(`<ul>${lis.join('')}</ul>`);
      continue;
    }

    htmlParts.push(`<p>${line}</p>`);
    i++;
  }

  // Post-process: remove orphaned headers
  const filtered: string[] = [];
  for (let j = 0; j < htmlParts.length; j++) {
    const isHeader = htmlParts[j].startsWith('<p><span style="font-weight: bold;">');
    if (isHeader) {
      const next = htmlParts[j + 1];
      if (!next || next.startsWith('<p><span style="font-weight: bold;">')) {
        continue;
      }
    }
    filtered.push(htmlParts[j]);
  }

  return filtered.join('');
}

export const buildSubjectiveFromTemplateHtml = (survey: PatientSurvey): string => {
  const text = buildSubjectiveFromTemplate(survey);
  if (!text) return '';

  const name = survey?.survey?.english?.name || survey?.survey?.name || '';
  const header = `<h3 style="font-weight: bold;">${name}</h3>`;

  // Check if template uses structured syntax (## headers or - bullets)
  const hasStructuredSyntax = text
    .split('\n')
    .some(line => line.trim().startsWith('## ') || line.trim().startsWith('- '));

  if (hasStructuredSyntax) {
    return header + renderTemplateLines(text);
  }

  // Legacy: Wrap each line in its own <p> tag.
  // Using <br /> won't survive the collaborative editor's stripTags.br() pass,
  // but <p> opening tags are preserved and TipTap treats them as paragraph breaks.
  // Blank lines use &nbsp; so they survive the stripTags.p empty-paragraph removal.
  const htmlBody = text
    .split('\n')
    .map(line => (line.trim() === '' ? '<p>&nbsp;</p>' : `<p>${line}</p>`))
    .join('');

  return header + htmlBody;
};
